/**
 * GraphIndex — Queryable property graph view of the live node graph.
 *
 * Maintains forward/reverse adjacency maps over GraphNodes and GraphEdges,
 * allowing nodes to query connections, traverse upstream/downstream, and
 * find nodes matching criteria — without payload propagation.
 *
 * Inspired by neo4j's property graph model.
 */

import type { INode } from '../nodes/INode';
import { logger } from '@/logging';

// ─────────────────────────────────────────────────────────────
// Data Model
// ─────────────────────────────────────────────────────────────

/** Indexed view of a live INode in the graph */
export interface GraphNode {
  /** Global key: `${instancePath}:${nodeId}` */
  key: string;
  instancePath: string;
  nodeId: string;
  /** Reference to the live node */
  node: INode;
  /** Classification labels derived from node type and capabilities */
  labels: Set<string>;
}

/** Where this edge comes from 
 * horizntal = same graph
 * vertical = cross graph
 * 
*/
export type EdgeSource = 'horizontal' | 'vertical' | 'field';

/** A directed edge between two GraphNodes */
export interface GraphEdge {
  source: EdgeSource;
  sourceKey: string;
  targetKey: string;
  sourceEndpointId: string;
  targetEndpointId: string;
  weight: number;
  /** When true, edge is ignored by graph queries (used by Switch nodes) */
  disabled?: boolean;
}

// ─────────────────────────────────────────────────────────────
// GraphIndex
// ─────────────────────────────────────────────────────────────

export class GraphIndex {
  /** All registered graph nodes, keyed by global key */
  private nodes = new Map<string, GraphNode>();

  /** Forward adjacency: nodeKey → outgoing edges */
  private forward = new Map<string, GraphEdge[]>();

  /** Reverse adjacency: nodeKey → incoming edges */
  private reverse = new Map<string, GraphEdge[]>();

  /** Simple query cache with version-based invalidation */
  private queryCache = new Map<string, any>();
  private cacheVersion = 0;

  /** Current graph version — increments on every topology change */
  get version(): number {
    return this.cacheVersion;
  }

  // ─── Key helpers ───────────────────────────────────────────

  nodeKey(instancePath: string, nodeId: string): string {
    return `${instancePath}:${nodeId}`;
  }

  // ─── Mutation (called by Runtime) ──────────────────────────

  addNode(instancePath: string, nodeId: string, node: INode): void {
    const key = this.nodeKey(instancePath, nodeId);
    const labels = this.deriveLabels(node);
    this.nodes.set(key, { key, instancePath, nodeId, node, labels });
    this.invalidateCache();
  }

  removeNode(instancePath: string, nodeId: string): void {
    const key = this.nodeKey(instancePath, nodeId);
    this.nodes.delete(key);

    // Remove all edges involving this node
    const fwd = this.forward.get(key);
    if (fwd) {
      for (const edge of fwd) {
        const rev = this.reverse.get(edge.targetKey);
        if (rev) {
          this.reverse.set(edge.targetKey, rev.filter(e => e.sourceKey !== key));
        }
      }
      this.forward.delete(key);
    }

    const rev = this.reverse.get(key);
    if (rev) {
      for (const edge of rev) {
        const fwdEdges = this.forward.get(edge.sourceKey);
        if (fwdEdges) {
          this.forward.set(edge.sourceKey, fwdEdges.filter(e => e.targetKey !== key));
        }
      }
      this.reverse.delete(key);
    }

    this.invalidateCache();
  }

  /** Remove all nodes and edges belonging to an instance */
  removeInstance(instancePath: string): void {
    const keysToRemove: string[] = [];
    for (const [key, gn] of this.nodes) {
      if (gn.instancePath === instancePath) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      const gn = this.nodes.get(key)!;
      this.removeNode(gn.instancePath, gn.nodeId);
    }
  }

  addEdge(edge: GraphEdge): void {
    const fwd = this.forward.get(edge.sourceKey);
    if (fwd) {
      const dup = fwd.some(e =>
        e.targetKey === edge.targetKey &&
        e.sourceEndpointId === edge.sourceEndpointId &&
        e.targetEndpointId === edge.targetEndpointId
      );
      if (dup) {
        logger.trace(`[GraphIndex] duplicate edge skipped: ${edge.sourceKey}.${edge.sourceEndpointId} → ${edge.targetKey}.${edge.targetEndpointId} (${edge.source})`);
        return;
      }
      fwd.push(edge);
    } else {
      this.forward.set(edge.sourceKey, [edge]);
    }

    const rev = this.reverse.get(edge.targetKey);
    if (rev) {
      rev.push(edge);
    } else {
      this.reverse.set(edge.targetKey, [edge]);
    }

    this.invalidateCache();
  }

  /** Add an internal edge crossing a subgraph boundary (exposed endpoint ↔ internal endpoint) */
  addSubgraphEdge(args: {
    parentInstancePath: string;
    subNodeId: string;
    subInstancePath: string;
    internalNodeId: string;
    exposedName: string;
    internalEndpointId: string;
    isOutput: boolean;
  }): void {
    const { parentInstancePath, subNodeId, subInstancePath, internalNodeId, exposedName, internalEndpointId, isOutput } = args;
    if (isOutput) {
      this.addEdge({
        source: 'vertical',
        sourceKey: this.nodeKey(subInstancePath, internalNodeId),
        targetKey: this.nodeKey(parentInstancePath, subNodeId),
        sourceEndpointId: internalEndpointId,
        targetEndpointId: exposedName,
        weight: 1
      });
    } else {
      this.addEdge({
        source: 'vertical',
        sourceKey: this.nodeKey(parentInstancePath, subNodeId),
        targetKey: this.nodeKey(subInstancePath, internalNodeId),
        sourceEndpointId: exposedName,
        targetEndpointId: internalEndpointId,
        weight: 1
      });
    }
  }

  /** Remove an internal edge crossing a subgraph boundary */
  removeSubgraphEdge(args: {
    parentInstancePath: string;
    subNodeId: string;
    subInstancePath: string;
    internalNodeId: string;
    exposedName: string;
    internalEndpointId: string;
    isOutput: boolean;
  }): void {
    const { parentInstancePath, subNodeId, subInstancePath, internalNodeId, exposedName, internalEndpointId, isOutput } = args;
    if (isOutput) {
      this.removeEdge(
        this.nodeKey(subInstancePath, internalNodeId),
        this.nodeKey(parentInstancePath, subNodeId),
        internalEndpointId,
        exposedName
      );
    } else {
      this.removeEdge(
        this.nodeKey(parentInstancePath, subNodeId),
        this.nodeKey(subInstancePath, internalNodeId),
        exposedName,
        internalEndpointId
      );
    }
  }

  removeEdge(sourceKey: string, targetKey: string, sourceEp: string, targetEp: string): void {
    const fwd = this.forward.get(sourceKey);
    if (fwd) {
      this.forward.set(sourceKey, fwd.filter(
        e => !(e.targetKey === targetKey && e.sourceEndpointId === sourceEp && e.targetEndpointId === targetEp)
      ));
    }

    const rev = this.reverse.get(targetKey);
    if (rev) {
      this.reverse.set(targetKey, rev.filter(
        e => !(e.sourceKey === sourceKey && e.sourceEndpointId === sourceEp && e.targetEndpointId === targetEp)
      ));
    }

    this.invalidateCache();
  }

  // ─── Queries ───────────────────────────────────────────────

  /** Get a registered node by its instance path and nodeId */
  getNode(nodeId: string, instancePath: string): GraphNode | undefined {
    return this.nodes.get(this.nodeKey(instancePath, nodeId));
  }

  /** Get a registered node by its global key */
  getNodeByKey(key: string): GraphNode | undefined {
    return this.nodes.get(key);
  }

  /** Get incoming edges for a node by its global key (excludes disabled edges) */
  getReverseEdges(key: string): GraphEdge[] {
    return (this.reverse.get(key) ?? []).filter(e => !e.disabled);
  }

  /** Get direct upstream edges for a node (excludes disabled edges) */
  getUpstream(nodeId: string, instancePath: string): GraphEdge[] {
    return (this.reverse.get(this.nodeKey(instancePath, nodeId)) ?? []).filter(e => !e.disabled);
  }

  /** Get direct downstream edges for a node (excludes disabled edges) */
  getDownstream(nodeId: string, instancePath: string): GraphEdge[] {
    return (this.forward.get(this.nodeKey(instancePath, nodeId)) ?? []).filter(e => !e.disabled);
  }

  /** Get ALL incoming edges for a node (including disabled) — used by Switch nodes to sync disabled state */
  getAllReverseEdges(key: string): GraphEdge[] {
    return this.reverse.get(key) ?? [];
  }

  /** Get ALL outgoing edges for a node (including disabled) — used by Switch nodes to sync disabled state */
  getAllForwardEdges(key: string): GraphEdge[] {
    return this.forward.get(key) ?? [];
  }

  /** Set disabled state on edges matching source/target keys and endpoint IDs */
  setEdgeDisabled(sourceKey: string, targetKey: string, targetEndpointId: string, disabled: boolean): void {
    const fwd = this.forward.get(sourceKey);
    if (fwd) {
      for (const e of fwd) {
        if (e.targetKey === targetKey && e.targetEndpointId === targetEndpointId) {
          e.disabled = disabled;
        }
      }
    }
    const rev = this.reverse.get(targetKey);
    if (rev) {
      for (const e of rev) {
        if (e.sourceKey === sourceKey && e.targetEndpointId === targetEndpointId) {
          e.disabled = disabled;
        }
      }
    }
    this.invalidateCache();
  }

  /** Get all nodes from instances that are descendants of the given instance path */
  getDescendantNodes(instancePath: string): GraphNode[] {
    const prefix = instancePath + '/';
    const result: GraphNode[] = [];
    for (const gn of this.nodes.values()) {
      if (gn.instancePath.startsWith(prefix)) {
        result.push(gn);
      }
    }
    return result;
  }

  /**
   * BFS upstream from a node, returning all nodes matching predicate.
   * Crosses subgraph boundaries via 'internal' edges transparently.
   */
  findUpstream(nodeId: string, instancePath: string, predicate: (node: INode) => boolean): INode[] {
    const startKey = this.nodeKey(instancePath, nodeId);
    const visited = new Set<string>();
    const queue: string[] = [startKey];
    const results: INode[] = [];

    visited.add(startKey);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const edges = this.reverse.get(current) ?? [];

      for (const edge of edges) {
        if (visited.has(edge.sourceKey)) continue;
        visited.add(edge.sourceKey);

        const gn = this.nodes.get(edge.sourceKey);
        if (gn && predicate(gn.node)) {
          results.push(gn.node);
        }

        queue.push(edge.sourceKey);
      }
    }

    return results;
  }

  /**
   * BFS downstream from a node, returning all nodes matching predicate.
   * Crosses subgraph boundaries via 'internal' edges transparently.
   */
  findDownstream(nodeId: string, instancePath: string, predicate: (node: INode) => boolean): INode[] {
    const startKey = this.nodeKey(instancePath, nodeId);
    const visited = new Set<string>();
    const queue: string[] = [startKey];
    const results: INode[] = [];

    visited.add(startKey);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const edges = this.forward.get(current) ?? [];

      for (const edge of edges) {
        if (visited.has(edge.targetKey)) continue;
        visited.add(edge.targetKey);

        const gn = this.nodes.get(edge.targetKey);
        if (gn && predicate(gn.node)) {
          results.push(gn.node);
        }

        queue.push(edge.targetKey);
      }
    }

    return results;
  }

  /**
   * Find animate root nodes upstream of a given node.
   *
   * An "animate root" is a node with an active animate callback (useExp endpoint
   * or onAnimate method) that has NO upstream ancestor that also has animate.
   *
   * Algorithm:
   * 1. BFS upstream, collecting all nodes with animate capability
   * 2. For each animate node, check if any of its upstream nodes also have animate
   * 3. Those without an animate ancestor are roots
   */
  findAnimateRoots(nodeId: string, instancePath: string): INode[] {
    const cacheKey = `animateRoots:${instancePath}:${nodeId}:${this.cacheVersion}`;
    const cached = this.queryCache.get(cacheKey);
    if (cached) return cached;

    const startKey = this.nodeKey(instancePath, nodeId);
    const visited = new Set<string>();
    const queue: string[] = [startKey];
    // All nodes with animate capability, keyed by their graph key
    const animateNodes = new Map<string, INode>();
    // Track which animate nodes have an animate ancestor
    const hasAnimateAncestor = new Set<string>();

    visited.add(startKey);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const edges = this.reverse.get(current) ?? [];

      // Is current node an animate node?
      const currentIsAnimate = animateNodes.has(current);

      for (const edge of edges) {
        if (visited.has(edge.sourceKey)) continue;
        visited.add(edge.sourceKey);

        const gn = this.nodes.get(edge.sourceKey);
        if (gn && this.hasAnimateCapability(gn.node)) {
          animateNodes.set(edge.sourceKey, gn.node);
          // If current is also animate, then this new animate node has an animate descendant
          // — but we need the reverse: does it have an animate *ancestor*?
        }

        queue.push(edge.sourceKey);
      }
    }

    // Second pass: for each animate node, check if any upstream node is also animate
    for (const [key] of animateNodes) {
      const upstreamVisited = new Set<string>();
      const upstreamQueue: string[] = [];
      upstreamVisited.add(key);

      // Seed with direct upstream
      for (const edge of (this.reverse.get(key) ?? [])) {
        if (!upstreamVisited.has(edge.sourceKey)) {
          upstreamVisited.add(edge.sourceKey);
          upstreamQueue.push(edge.sourceKey);
        }
      }

      let foundAncestor = false;
      while (upstreamQueue.length > 0 && !foundAncestor) {
        const cur = upstreamQueue.shift()!;
        if (animateNodes.has(cur)) {
          foundAncestor = true;
          break;
        }
        for (const edge of (this.reverse.get(cur) ?? [])) {
          if (!upstreamVisited.has(edge.sourceKey)) {
            upstreamVisited.add(edge.sourceKey);
            upstreamQueue.push(edge.sourceKey);
          }
        }
      }

      if (foundAncestor) {
        hasAnimateAncestor.add(key);
      }
    }

    // Roots = animate nodes without an animate ancestor
    const roots: INode[] = [];
    for (const [key, node] of animateNodes) {
      if (!hasAnimateAncestor.has(key)) {
        roots.push(node);
      }
    }

    this.queryCache.set(cacheKey, roots);
    return roots;
  }

  /** Bump version to signal capability changes (e.g. useExp toggled) without topology change */
  touch(): void {
    this.invalidateCache();
  }

  // ─── Internal helpers ──────────────────────────────────────

  private invalidateCache(): void {
    this.cacheVersion++;
    this.queryCache.clear();
  }

  /** Derive labels from a node's type and capabilities */
  private deriveLabels(node: INode): Set<string> {
    const labels = new Set<string>();
    const type = node.type;
    if (type) {
      // Split qualified type (e.g., 'three.Transform' → 'three', 'transform')
      const parts = type.split('.');
      for (const part of parts) {
        labels.add(part.toLowerCase());
      }
    }
    if (this.hasAnimateCapability(node)) {
      labels.add('animate');
    }
    return labels;
  }

  /** Check if a node has animate capability (function endpoints, onAnimate method, or needsPerFrameUpdate) */
  private hasAnimateCapability(node: INode): boolean {
    return typeof (node as any).onAnimate === 'function'
      || (typeof (node as any).hasFunctionParams === 'function' && (node as any).hasFunctionParams())
      || (node as any).needsPerFrameUpdate === true;
  }
}
